import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "lucide-react";
import ScrollReveal from "../ScrollReveal";
import { PORTFOLIO } from "../../data/content";

/**
 * Infinite-scroll portfolio carousel with auto-aspect-ratio cards.
 * Each card preserves the image's natural aspect via CSS aspect-ratio,
 * and width adapts so different image shapes look balanced.
 */

const widthForAspect = (aspect) => {
  // Returns Tailwind width classes tuned per aspect ratio.
  // Goal: portrait cards narrower, landscape wider, square in between.
  switch (aspect) {
    case "3/4":
    case "4/5":
      return "w-[300px] md:w-[420px]"; // portrait
    case "1/1":
      return "w-[340px] md:w-[480px]"; // square
    case "16/9":
    case "16/10":
      return "w-[400px] md:w-[640px]"; // wide
    case "4/3":
    default:
      return "w-[360px] md:w-[560px]"; // standard
  }
};

const Card = ({ p }) => (
  <div
    data-testid={`portfolio-item-${p.id}`}
    className={`group relative mx-3 md:mx-4 ${widthForAspect(p.aspect)} shrink-0 rounded-3xl overflow-hidden border border-white/10 bg-[#080808] hover:border-violet-400/60 hover:shadow-[0_0_50px_rgba(139,92,246,0.45)] transition-all duration-500`}
  >
    <div
      className="relative overflow-hidden"
      style={{ aspectRatio: p.aspect || "4/3" }}
    >
      <img
        src={p.image}
        alt={p.client}
        className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/0 via-black/0 to-transparent" />
      <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] bg-black/70 backdrop-blur px-3 py-1.5 rounded-full border border-violet-400/40 text-violet-200">
        {p.tag}
      </div>
    </div>
    <div className="p-5 md:p-6 flex items-center justify-between">
      <div>
        <div className="font-display text-base md:text-xl font-extrabold uppercase tracking-tight">
          {p.client}
        </div>
        <div className="text-xs text-white/50 tracking-wider mt-1">
          {p.category}
        </div>
      </div>
      <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-violet-500 group-hover:border-violet-500 group-hover:text-white transition-colors">
        <ArrowUpRight size={14} />
      </div>
    </div>
  </div>
);

export const PortfolioGrid = () => {
  const items = PORTFOLIO;
  // Split into two ribbons by alternating index (so each ribbon has variety)
  const ribbon1 = items.filter((_, i) => i % 2 === 0);
  const ribbon2 = items.filter((_, i) => i % 2 === 1);

  return (
    <section
      data-testid="portfolio-grid-section"
      className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <ScrollReveal className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-violet-300/80 mb-4">
              / Selected Work
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1]">
              Portfolio<br /><span className="text-violet-glow">in motion.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
            150+ projects · 60+ brands · 5 years. Hover to pause — every card
            adapts to its own aspect ratio for cinematic balance.
          </p>
        </ScrollReveal>
      </div>

      {/* Carousel ribbon 1 */}
      <Marquee
        gradient
        gradientColor="#050505"
        gradientWidth={120}
        speed={45}
        pauseOnHover
        className="py-4"
      >
        {ribbon1.concat(ribbon1, ribbon1).map((p, i) => (
          <Card key={`a-${p.id}-${i}`} p={p} />
        ))}
      </Marquee>

      {/* Carousel ribbon 2 — opposite direction */}
      <Marquee
        gradient
        gradientColor="#050505"
        gradientWidth={120}
        speed={35}
        direction="right"
        pauseOnHover
        className="py-4 mt-2"
      >
        {ribbon2.concat(ribbon2, ribbon2).map((p, i) => (
          <Card key={`b-${p.id}-${i}`} p={p} />
        ))}
      </Marquee>
    </section>
  );
};

export default PortfolioGrid;
